import { Extension } from "@tiptap/react";
import {
  goBack,
  jumpBackwardSentence,
  jumpBackwardWord,
  jumpForwardSentence,
  jumpForwardWord,
  nextSentence,
  nextWord,
  usePrewrittenText,
} from "PreWritten";
import { useEditor } from "./EditorStore";
import { Item } from "@aws-sdk/client-transcribe-streaming";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    customCommands: {
      /**
       * Move cursor to end of document
       */
      moveCursorToEnd: () => ReturnType;
      /**
       * Abbreviate word at current selection, then add a word-joiner after
       */
      addWordJoiner: () => ReturnType;
      /**
       * Insert word from pre-written text
       */
      insertWord: () => ReturnType;
      /**
       * Go a word back in pre-written text
       */
      removeWord: () => ReturnType;
      /**
       * Insert line from pre-written text
       */
      insertLine: () => ReturnType;
      /**
       * Go back a line in pre-written text
       */
      removeLine: () => ReturnType;
      /**
       * Jump forward a word in pre-written text
       */
      jumpWordForward: () => ReturnType;
      /**
       * Jump backwards a word in pre-written text
       */
      jumpWordBack: () => ReturnType;
      /**
       * Jump forward a line in pre-written text
       */
      jumpLineForward: () => ReturnType;
      /**
       * Jump backwards a line in pre-written text
       */
      jumpLineBack: () => ReturnType;
      /**
       * Insert a text-string but replace \n with simulated
       * enter-presses to get proper paragraphs
       */
      insertTextWithLinebreaks: (text: string) => ReturnType;
      /**
       * Focus the editor and put the cursor back where it was based on last onBlur
       * This is done automatically if event.relatedTarget is set, but that's not the case
       * when giving focus back after closing the settings menu for example
       */
      refocus: () => ReturnType;
      /**
       * Clear the text view from text by moving current text into transcription and then emptying the content
       */
      clearAllText: () => ReturnType;
      /**
       * Add transcribed word to the editor
       * @param word - The word to add to the transcript
       */
      addTranscriptWord: (item: Item, newLine: number) => ReturnType;
      /**
       * Update transcribed word that has changed
       * @param from - Old word
       * @param to - New word
       * @param index - Position of the word counting from the end and backwards
       */
      updateTranscriptWord: (
        from: string,
        to: string,
        index: number,
        removeWordsAfter: boolean,
      ) => ReturnType;
    };
  }
}

let lastSpeaker = "";

export default Extension.create({
  name: "customCommands",
  addCommands() {
    return {
      moveCursorToEnd:
        () =>
        ({ chain }) =>
          chain().focus("end").scrollIntoView().run(),
      addWordJoiner:
        () =>
        ({ chain }) =>
          chain().abbreviateAtSelection().insertContent("\u2060").run(),
      insertWord:
        () =>
        ({ chain }) => {
          if (!usePrewrittenText.getState().active) {
            return false;
          }

          return chain()
            .focus()
            .moveCursorToEnd()
            .command(({ commands, editor }) => {
              const lastLine = editor.state.doc.lastChild!.textContent;
              const word = nextWord();
              if (!word) {
                return true;
              }
              if (
                word.startsWith("\n") ||
                lastLine === "" ||
                lastLine.endsWith(" ")
              ) {
                return commands.insertTextWithLinebreaks(word);
              } else {
                return commands.insertTextWithLinebreaks(" " + word);
              }
            })
            .run();
        },
      removeWord:
        () =>
        ({ chain }) => {
          if (!usePrewrittenText.getState().active) {
            return false;
          }

          return chain()
            .focus()
            .moveCursorToEnd()
            .command(({ tr, commands }) => {
              const lastLine = tr.doc.lastChild!;
              const pos = tr.selection.$from.pos;
              const lineStartIndex = pos - tr.selection.$from.parentOffset;
              const adjust = tr.selection.$from.pos - pos;
              let start = pos;
              let content = "";
              while (start > 0 && start > lineStartIndex) {
                content = tr.doc.textBetween(start, pos);
                if (content.trim() !== "" && content.search(/^. /) !== -1) {
                  content = content.slice(1);
                  ++start;
                  break;
                }
                --start;
              }

              if (start === 0 || start === lineStartIndex) {
                goBack("\n" + lastLine.textContent);
                return commands.deleteNode("paragraph");
              }

              goBack(content);
              tr.deleteRange(start + adjust, pos + adjust);
              return true;
            })
            .run();
        },
      insertLine:
        () =>
        ({ chain }) => {
          if (!usePrewrittenText.getState().active) {
            return false;
          }

          const lastLine = this.editor.state.doc.lastChild!.textContent;
          const word = nextSentence();
          if (!word) {
            return true;
          }
          if (
            word.startsWith("\n") ||
            lastLine === "" ||
            lastLine.endsWith(" ")
          ) {
            return chain()
              .moveCursorToEnd()
              .insertTextWithLinebreaks(word)
              .run();
          } else {
            return chain()
              .moveCursorToEnd()
              .insertTextWithLinebreaks(" " + word)
              .run();
          }
        },
      removeLine:
        () =>
        ({ chain }) => {
          if (!usePrewrittenText.getState().active) {
            return false;
          }

          const lastLine = this.editor.state.doc.lastChild!;
          goBack("\n" + lastLine.textContent);
          return chain().moveCursorToEnd().deleteNode("paragraph").run();
        },
      insertTextWithLinebreaks:
        (text: string) =>
        ({ chain }) => {
          if (!text) {
            return true;
          }

          const lines = text.split("\n");
          let myChain = chain();
          for (let i = 0; i < lines.length; ++i) {
            if (lines[i]) {
              myChain = myChain.insertContent(lines[i]);
            }
            if (i !== lines.length - 1) {
              myChain = myChain.enter();
            }
          }
          return myChain.run();
        },
      clearAllText:
        () =>
        ({ commands }) => {
          let content = [];
          const { ydoc } = useEditor.getState();
          const current = ydoc.getXmlFragment("current");
          for (let i = 0; i < current.length; ++i) {
            content.push(current.get(i).toJSON());
          }
          const transcription = ydoc.getArray("transcription");
          const size = transcription.length;
          commands.setContent("");
          transcription.insert(size, content);
          return true;
        },
      addTranscriptWord:
        (item: Item, newLine: number) =>
        ({ editor, chain }) => {
          let word = item.Content || "";
          const lastLine = editor.state.doc.lastChild;
          if (!lastLine) {
            return chain().insertContent(word).moveCursorToEnd().run();
          }
          const moveCursorToEnd =
            editor.isFocused &&
            editor.state.selection.from > editor.state.doc.content.size - 2;
          const lastChar = lastLine.textContent.at(-1);
          const myChain = chain();

          if (
            shouldCapitalize(lastLine.textContent, lastLine.textContent.length)
          ) {
            word = word.charAt(0).toUpperCase() + word.slice(1);
          }

          const onNewLine =
            newLine > 1 ||
            (newLine === 1 && [".", "?", "!"].includes(lastChar || ""));

          const insertPos = editor.state.doc.content.size - 1;

          if (onNewLine) {
            // Inserting a literal "\n" doesn't start a new paragraph node -
            // it just gets embedded as a character inside the existing one,
            // which then breaks anything parsing the document's XML (e.g.
            // the captions endpoint). Insert an actual paragraph node so it
            // splits the document like a real Enter keypress would.
            myChain.insertContentAt(
              insertPos,
              {
                type: "paragraph",
                content: word ? [{ type: "text", text: word }] : [],
              },
              { updateSelection: false }
            );
          } else if (
            item.Type === "punctuation" ||
            !lastChar ||
            (lastChar && [" ", "\xa0"].includes(lastChar))
          ) {
            myChain.insertContentAt(insertPos, word, {
              updateSelection: false,
            });
          } else {
            myChain.insertContentAt(insertPos, ` ${word}`, {
              updateSelection: false,
            });
          }
          if (moveCursorToEnd) {
            myChain.moveCursorToEnd();
          }
          return myChain.run();
        },
      updateTranscriptWord:
        (from: string, to: string, index: number, removeWordsAfter: boolean) =>
        ({ commands, editor }) => {
          const lastLine = editor.state.doc.lastChild;
          if (!lastLine) {
            return commands.insertContent(to);
          }
          const index = lastLine.textContent
            .toLocaleLowerCase()
            .lastIndexOf(from.toLocaleLowerCase());
          if (index === -1) {
            return true;
          }
          if (shouldCapitalize(lastLine.textContent, index)) {
            to = to.charAt(0).toUpperCase() + to.slice(1);
          }
          const fromPunctuation = isPunctuation(from);
          const toPunctuation = isPunctuation(to);
          if (fromPunctuation && !toPunctuation) {
            to = " " + to;
          }
          const posToSubtract = lastLine.textContent.length - index;
          // If we are replacing a non-punctuation with a punctuation we need to remove the space before.
          const extraAdjust =
            toPunctuation && !fromPunctuation && index > 0 ? 1 : 0;
          const pos =
            editor.state.doc.content.size - posToSubtract - 1 - extraAdjust;
          return commands.insertContentAt(
            {
              from: pos,
              to: removeWordsAfter
                ? editor.state.doc.content.size
                : pos + from.length + extraAdjust,
            },
            to,
            {
              updateSelection: false,
            },
          );
        },
    };
  },
  addKeyboardShortcuts() {
    return {
      Escape: ({ editor }) => editor.commands.moveCursorToEnd(),
      ArrowLeft: ({ editor }) => editor.commands.removeWord(),
      ArrowRight: ({ editor }) => editor.commands.insertWord(),
      ArrowUp: ({ editor }) => editor.commands.removeLine(),
      ArrowDown: ({ editor }) => editor.commands.insertLine(),
      "alt-ArrowLeft": () => {
        jumpBackwardWord();
        return true;
      },
      "alt-ArrowRight": () => {
        jumpForwardWord();
        return true;
      },
      "alt-ArrowDown": () => {
        jumpForwardSentence();
        return true;
      },
      "alt-ArrowUp": () => {
        jumpBackwardSentence();
        return true;
      },
      "mod-Enter": () => {
        const { provider, ydoc } = useEditor.getState();
        if (provider) {
          const state = provider.awareness?.getStates();
          const stateData = Array.from<any>(state?.entries() || []);
          const current = provider.awareness?.getLocalState()?.change;
          const data = Array.from<any>(state?.values() || []);
          const numberOfChangeRequests = data.filter(
            (val) => val.change,
          ).length;
          if (current) {
            provider.awareness?.setLocalStateField("change", false);
          } else if (numberOfChangeRequests > 0) {
            const active = ydoc.getText("active");
            const myId = ydoc.clientID;
            if (active.toJSON() === myId.toString()) {
              const other = stateData.find(
                (val) => val[0] !== myId && val[1]?.["change"],
              );
              const otherId = other?.[0].toString();
              if (otherId) {
                active.delete(0, active.length);
                active.insert(0, otherId);
              }
            } else {
              active.delete(0, active.length);
              active.insert(0, myId.toString());
            }
            provider.awareness?.setLocalStateField("changeTime", Date.now());
          } else {
            provider.awareness?.setLocalStateField("change", true);
          }
        }
        return true;
      },
    };
  },
});

function shouldCapitalize(line: string, pos: number) {
  if (pos < 1) return true;
  while (pos > 1 && [" ", "\xa0"].includes(line[pos - 1])) {
    --pos;
  }
  if (line[pos - 1]?.match(/[.!?\s]/)) return true;
  return false;
}

function isPunctuation(text: string) {
  return text === "." || text === ",";
}
